import React, { Component } from 'react';
import _ from 'lodash';
import i18n from 'i18n-js';
import { Table, Icon, Header } from 'semantic-grab-ui/src';

const invertedColStyle = { width: '30%', fontWeight: '400' };

interface TableRowProps {
  values: any[];
  inverted?: boolean;
}

const TableRow: React.FC<TableRowProps> = ({ values, inverted }) => (
  <Table.Row>{
    _.map(values, (value, cellIndex) => (
      <Table.Cell
        key={`cell-${cellIndex}`}
        style={(!cellIndex && !!inverted) ? invertedColStyle : {}}
      >
        {value}
      </Table.Cell>
    ))
  }</Table.Row>
);

interface TableBodyProps {
  rows: any[][];
  inverted?: boolean;
}

const TableBody: React.FC<TableBodyProps> = ({ rows, inverted }) => (
  <Table.Body>
    {rows.map((row, index) => (
      <TableRow key={`row-${index}`} values={row} inverted={inverted} />
    ))}
  </Table.Body>
);

const defaultTableProps = { striped: true };

interface Column {
  key?: string;
  name?: string;
  renderFunc?: (row: any, index: number) => any;
  sort?: string;
}

interface Props {
  columns: Array<Column | string>;
  rows: Array<any>;
  localeSelector?: string;
  tableProps?: any;
  inverted?: boolean;
  onSort: (key: string) => void;
}

interface ColumnObj {
  key: string;
  name: string;
  renderFunc: (row: any, index: number) => any;
  sort: string;
}

export default class TableComponent extends Component<Props> {
  render(): JSX.Element {
    const { rows, columns, localeSelector, inverted, tableProps } = this.props;
    // convert all column items to valid column objects
    const columnObjs: ColumnObj[] = _.map(_.filter(columns, (column) => !!column), (column) => {
      const obj: ColumnObj = {} as ColumnObj;
      // Get key from column object, else column is not the object but the key string itself
      obj.key = _.get(column, 'key', column as string);
      // Get name from column.name, or from locales after prefixing localeSelector or at last resort just use the startCased key
      obj.name = _.get(column, 'name',
        (localeSelector && i18n.t(`${localeSelector}.${obj.key}`, { defaultValue: _.startCase(obj.key) }))
        || _.startCase(obj.key));
      // Get the renderFunc if present or set it as keyGetter from row
      obj.renderFunc = _.get(column, 'renderFunc', (row: any) => _.get(row, obj.key));
      obj.sort = _.get(column, 'sort', '');
      return obj;
    });
    let tableData = _.filter(_.map(rows, (row, index) => _.map(columnObjs, (column) => column.renderFunc(row, index))), _.some);
    if (inverted) {
      // Transpose the tableData matrix, include first column (row headers) from columnObjs
      tableData = _.map(tableData[0], (y, i) => [columnObjs[i].name, ..._.map(tableData, x => x[i])]);
    }

    const sortIcons: Record<string, string> = {
      dsc: 'sort down',
      asc: 'sort up',
    };

    return (
      <Table {...{ ...defaultTableProps, ...tableProps }} definition={inverted}>
        {/* When inverted, column headers become row headers i.e. they are row's first element */}
        {!inverted && <Table.Header>
          <Table.Row>
            {_.map(columnObjs, (column, index) => (
              <Table.HeaderCell key={`cell-${index}`}>
                {column.name} <Icon name={_.get(sortIcons, column.sort, 'sort')} link onClick={() => { this.props.onSort(column.key); }} />
              </Table.HeaderCell>
            )
            )}
          </Table.Row>
        </Table.Header>}
        {tableData.length > 0
          ? <TableBody
            inverted={inverted}
            rows={tableData}
          />
          : (
            <Table.Footer fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan={columnObjs.length}>
                  <Header textAlign="center">{i18n.t('common.no_results')}</Header>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>)
        }
      </Table>
    );
  }
}
